import React, { useEffect, Fragment, useState, useRef } from 'react';
import {
  Table,
  Dropdown,
  Menu,
  Switch,
  Tooltip,
  Button,
  Popover,
  Typography,
  Row,
  Col,
} from 'antd';
import Icon, {
  UserOutlined,
  EllipsisOutlined,
  UpOutlined,
  DownOutlined,
  BarChartOutlined,
  StopOutlined,
} from '@ant-design/icons';
import styles from './monitoring.module.sass';
import { ReactComponent as shared_icon } from '../../../../assets/icons/SharedIcon.svg';
import { ReactComponent as global_feed_icon } from '../../../../assets/icons/GlobalFeedIcon.svg';
import { ReactComponent as description_icon } from '../../../../assets/icons/notes.svg';
import { STATUS } from '../../../../redux/constants';
import { useDispatch } from 'react-redux';
import { changeTitle } from '../../../../redux/actions/app';
import VersionCheck from '../../../../shared-components/VersionCheck';
import Monitor from '../../../../shared-components/Monitor';
import { ReactComponent as no_results } from '../../../../assets/icons/no_results.svg';
import { ColumnTitle } from '../../../../shared-components/ColumnTitle';
import ResultsHistory from '../ResultsHistory';
import { confirmDelete } from '../../../../shared-components/DeleteConfirm';
import PermissionDeniedPage from '../../../../shared-components/PermissionDeniedPage';
import LoadingSilentPush from '../../../../shared-components/LoadingSilentPush';
import MonitoringBarChart from '../MonitoringBarChart';
import { incrementUIRequestsCounter } from '../../../../redux/utils/websocket/incrementUIRequestsCounter';

const DescriptionIcon = (props) => (
  <Icon style={{ fontSize: '14px' }} component={description_icon} {...props} />
);

const SharedIcon = (props) => (
  <Icon style={{ fontSize: '14px' }} component={shared_icon} {...props} />
);

const GlobalIcon = (props) => (
  <Icon style={{ fontSize: '10px' }} component={global_feed_icon} {...props} />
);

function Monitoring(props) {
  const {
    loadUser,
    loadMySubscriptionPlan,
    loadMonitoringQueries,
    monitoringQueries,
    monitoringQueriesCount,
    monitoringQueriesStatus,
    deleteMonitoringQuery,
  } = props;
  const [showEditMonitorModal, setShowEditMonitorModal] = useState(false);
  const [selectedMonitorToEdit, setSelectedMonitorToEdit] = useState({});
  const { Text, Title } = Typography;
  const [expandedRowsKeys, setExpandedRowsKeys] = useState([]);
  const expandedRowUUID = useRef(null);
  const [historyDataSource, setHistoryDataSource] = useState([]);
  const selectedMonitorToActivate = useRef(null);
  const onHoverSelectedMonitor = useRef(null);
  const [expandedChartData, setExpandedChartData] = useState([]);
  const [onHoverChartData, setOnHoverChartData] = useState({});
  const currentPage = useRef(1);

  useEffect(() => {
    document.title = 'Monitored Queries - Silent Push';
    loadUser();
    loadMySubscriptionPlan();
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setHistoryDataSource(props?.monitoringQueryResultHistories);
    if (
      props?.monitoringQueryResultHistories &&
      expandedRowUUID.current !== null
    ) {
      let chartData = [];

      for (let result of props?.monitoringQueryResultHistories) {
        chartData.push({
          date: result?.created?.substring(0, 10),
          observables: result?.iocs_count,
        });
      }

      setExpandedChartData(chartData);
    }

    if (
      props?.monitoringQueryResultHistories &&
      onHoverSelectedMonitor.current !== null
    ) {
      let chartData = [];

      for (let result of props?.monitoringQueryResultHistories) {
        chartData.push({
          date: result?.created?.substring(0, 10),
          observables: result?.iocs_count,
        });
      }

      setOnHoverChartData(chartData);
    }
  }, [props?.monitoringQueryResultHistories]);

  const fetchData = () => {
    loadMonitoringQueries(currentPage.current);
    incrementUIRequestsCounter();
  };

  function sharingIconSelector(record) {
    if (record?.scope === 'USER') {
      return <UserOutlined style={{ marginRight: '8px' }} />;
    } else if (record?.scope === 'ORGANISATION') {
      return <SharedIcon style={{ marginRight: '8px' }} />;
    } else if (record?.scope === 'GLOBAL') {
      return <GlobalIcon style={{ marginRight: '8px' }} />;
    }
  }

  function sharingTextSelector(record) {
    if (record?.scope === 'USER') {
      return 'Share Monitor';
    } else if (record?.is_mine === true && record?.scope === 'ORGANISATION') {
      return 'Unshare Monitor';
    }
  }

  function changeScopeMonitoringQuery(record) {
    const monitor = props?.monitoringQueries?.filter(
      (monitor) => monitor.uuid === record?.uuid,
    );
    props.changeScopeMonitoringQuery({
      scope: monitor[0]?.scope === 'USER' ? 'ORGANISATION' : 'USER',
      uuid: monitor[0]?.uuid,
    });
  }

  const getMonitoringQueryFullURL = (url) => {
    if (url) {
      const host = new URL(process.env.REACT_APP_API_URL);
      let full_url = host;
      if (url.includes('explore-recipes/')) {
        full_url = host + 'api/v1/' + url;
      } else {
        full_url = host + 'api/v1/merge-api/explore/' + url;
      }

      if (full_url) {
        const query_params_start_index = full_url.indexOf('?');
        if (query_params_start_index) {
          const query_params = full_url.substring(query_params_start_index);
          if (query_params?.length > 65) {
            let new_full_url = full_url.substring(0, query_params_start_index);
            for (let i = 0; i < query_params.length; i = i + 65) {
              new_full_url = new_full_url + query_params.substring(i, i + 65);
            }
            return new_full_url;
          }
        }
        return full_url;
      }
    }
    return '';
  };

  const columns = [
    {},
    {
      title: <ColumnTitle columnName="Monitor Active" />,
      render: (record) => (
        <Switch
          disabled={!record?.is_enabled}
          loading={
            props?.updateMonitorStatus === STATUS.LOADING &&
            selectedMonitorToActivate.current == record?.uuid
          }
          checked={record?.is_active}
          onChange={() => {
            selectedMonitorToActivate.current = record?.uuid;
            props.updateMonitoringQuery({
              uuid: record?.uuid,
              is_active: !record?.is_active,
            });
            incrementUIRequestsCounter();
          }}
        />
      ),
    },
    {
      title: <ColumnTitle columnName="Monitor Name" />,
      dataIndex: 'name',
      key: 'name',
      align: 'left',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text, record) => (
        <span>
          {sharingIconSelector(record)}
          {record?.name}
          {record?.description ? (
            <Tooltip placement="bottom" title={record?.description}>
              &nbsp;
              <DescriptionIcon />
            </Tooltip>
          ) : (
            ''
          )}
        </span>
      ),
    },
    {
      title: <ColumnTitle columnName="Monitor query URL" />,
      dataIndex: 'url',
      key: 'url',
      align: 'left',
      render: (text, record) => (
        <Popover content={getMonitoringQueryFullURL(record?.url)}>
          {text?.length > 50 ? (
            <Text>{text?.substring(0, 50)}...</Text>
          ) : (
            <Text>{text}</Text>
          )}
        </Popover>
      ),
      sorter: (a, b) => a.url.localeCompare(b.url),
    },
    {
      title: <ColumnTitle columnName="Query type" />,
      dataIndex: 'query_type',
      key: 'query_type',
      align: 'left',
      sorter: (a, b) => a?.query_type?.localeCompare(b?.query_type),
    },
    {
      title: <ColumnTitle columnName="Created by" />,
      dataIndex: ['created_by', ['name']],
      key: 'created_by',
      align: 'left',
      sorter: (a, b) => a?.created_by?.name.localeCompare(b?.created_by?.name),
    },
    {
      title: <ColumnTitle columnName="History" />,
      dataIndex: ['history'],
      key: 'history',
      align: 'left',
    },
    {
      title: '',
      dataIndex: [''],
      key: '',
      align: 'left',
      render: (text, record) =>
        expandedRowUUID.current === null ? (
          <Popover
            placement="leftBottom"
            overlayClassName="onHoverChart"
            content={(e) => getBarGraph(e)}
            onMouseEnter={() => showOnHoverChart(record)}
          >
            <BarChartOutlined />
          </Popover>
        ) : (
          <BarChartOutlined />
        ),
    },
    {
      title: '',
      key: 'actions',
      width: '80px',
      render: (record) => (
        <div className={styles.action_buttons}>
          <Tooltip title="View Results History">
            <Button
              type="link"
              onClick={() => {
                if (expandedRowUUID.current === record?.uuid) {
                  expandedRowUUID.current = null;
                } else {
                  expandedRowUUID.current = record?.uuid;
                }
                onTableRowExpand(expandedRowUUID.current);
              }}
            >
              History
            </Button>
          </Tooltip>

          {isThreeDotsMenuEnabled(record) ? (
            <Dropdown
              className={styles.dots}
              trigger={['click']}
              overlay={<DropdownMenu record={record} />}
            >
              <EllipsisOutlined />
            </Dropdown>
          ) : (
            <Tooltip
              title={<>The recipe is disabled due to lack of permissions.</>}
              placement="left"
            >
              <Dropdown
                disabled={true}
                className={styles.dots}
                trigger={['click']}
                overlay={<DropdownMenu record={record} />}
              >
                <EllipsisOutlined />
              </Dropdown>
            </Tooltip>
          )}
        </div>
      ),
    },
  ];

  const getBarGraph = (e) => {
    if (onHoverChartData?.length > 0) {
      return (
        <div className={styles.onHoverGraph}>
          <MonitoringBarChart data={onHoverChartData} height={'23rem'} />
        </div>
      );
    } else {
      return (
        <div className={styles.noDataOnHover}>
          <StopOutlined />
          <Title level={4}>No data available</Title>
        </div>
      );
    }
  };

  const showOnHoverChart = (record) => {
    if (expandedRowUUID.current === null) {
      onHoverSelectedMonitor.current = record;
      props.loadMonitoringQueryResultsHistory(record?.uuid);
    }
  };

  const handleEditClick = (record) => {
    setShowEditMonitorModal(true);
    setSelectedMonitorToEdit(record?.record);
  };

  const handleModalCancel = () => {
    setShowEditMonitorModal(false);
    setSelectedMonitorToEdit({});
  };

  const isThreeDotsMenuEnabled = (record) => {
    return (
      record?.scope === 'USER' ||
      (record?.is_mine === true && record?.scope === 'ORGANISATION') ||
      record?.is_enabled
    );
  };

  const DropdownMenu = (record) => {
    return (
      <Menu className="ant-dropdown-menu">
        {record?.record?.is_enabled ? (
          <Menu.Item
            style={{ height: '32px', marginTop: '0px', marginBottom: '8px' }}
            className="ant-dropdown-menu-item"
            onClick={() => {
              navigator.clipboard.writeText(
                getMonitoringQueryFullURL(record?.record?.url),
              );
            }}
          >
            Copy API Endpoint
          </Menu.Item>
        ) : (
          ''
        )}

        {record?.record?.is_enabled ? (
          <Menu.Item
            style={{ height: '32px', marginTop: '0px', marginBottom: '8px' }}
            className="ant-dropdown-menu-item"
            onClick={() => {
              handleEditClick(record);
            }}
          >
            Edit Monitor Metadata
          </Menu.Item>
        ) : (
          ''
        )}

        {(record?.record?.scope === 'USER' ||
          (record?.record?.is_mine === true &&
            record?.record?.scope === 'ORGANISATION')) &&
        record?.record?.is_enabled ? (
          props.mySubscriptionPlan?.has_access_to_share_unshare ? (
            <Menu.Item
              style={{ height: '32px', marginTop: '0px', marginBottom: '8px' }}
              className="ant-dropdown-menu-item"
              onClick={async () => {
                await changeScopeMonitoringQuery(record?.record);
                incrementUIRequestsCounter();
              }}
            >
              <p>{sharingTextSelector(record?.record)}</p>
            </Menu.Item>
          ) : (
            <Tooltip
              title={
                <>
                  Sharing option is not part of this subscription plan. To
                  upgrade your subscription contact us at{' '}
                  <a target="_blank" href="mailto:sales@silentpush.com">
                    sales@silentpush.com
                  </a>
                  .
                </>
              }
              placement="left"
            >
              <Menu.Item
                style={{
                  height: '32px',
                  marginTop: '0px',
                  marginBottom: '8px',
                }}
                className="ant-dropdown-menu-item disabled"
              >
                <p>{sharingTextSelector(record?.record)}</p>
              </Menu.Item>
            </Tooltip>
          )
        ) : (
          ''
        )}

        {record?.record?.scope === 'USER' ||
        (record?.record?.is_mine === true &&
          record?.record?.scope === 'ORGANISATION') ? (
          <Menu.Item
            style={{ height: '32px', marginTop: '0px', marginBottom: '8px' }}
            className="ant-dropdown-menu-item"
            onClick={() => {
              confirmDelete({
                onOk: () => {
                  deleteMonitoringQuery(record?.record?.uuid);
                  incrementUIRequestsCounter();
                },
              });
            }}
          >
            <p style={{ color: 'red' }}>Delete</p>
          </Menu.Item>
        ) : (
          ''
        )}
      </Menu>
    );
  };

  function onTableRowExpand(expandedRowUUID) {
    const keys = [];
    if (expandedRowUUID) {
      keys.push(`${expandedRowUUID}`);
      props.loadMonitoringQueryResultsHistory(expandedRowUUID);
      incrementUIRequestsCounter();
    } else {
      setHistoryDataSource([]);
    }
    setExpandedRowsKeys(keys);
  }

  const NoDataIcon = (props) => <Icon component={no_results} {...props} />;
  const dispatch = useDispatch();
  dispatch(changeTitle('Monitored Queries', null));

  return props.mySubscriptionPlanStatus === STATUS.LOADED ? (
    props.mySubscriptionPlan?.has_access_to_monitoring ? (
      <div className={styles.monitoring}>
        <Fragment>
          <div className={styles.feed_container}>
            {monitoringQueriesStatus === STATUS.LOADED ? (
              <>
                <Table
                  showSorterTooltip={false}
                  columns={columns}
                  dataSource={monitoringQueries}
                  pagination={{
                    total: monitoringQueriesCount,
                    current: currentPage.current,
                    showSizeChanger: false,
                    pageSize: 100,
                    onChange: (current, pageSize) => {
                      currentPage.current = current;
                      fetchData();
                      window.scrollTo(0, 0);
                    },
                    position: ['bottomRight'],
                  }}
                  locale={{
                    emptyText: (
                      <div className={styles.empty_table}>
                        <NoDataIcon style={{ fontSize: 32 }} />
                        <Title level={4}>Monitoring page is empty</Title>
                        <Text>
                          You don't have any saved monitors yet. Go to explore
                          or any recipe to save new monitors from your queries.
                        </Text>
                      </div>
                    ),
                  }}
                  onExpand={(expanded, record) => {
                    if (expanded) {
                      expandedRowUUID.current = record?.uuid;
                    } else {
                      expandedRowUUID.current = null;
                    }
                    onTableRowExpand(expandedRowUUID.current);
                  }}
                  rowKey={(record) => `${record.uuid}`}
                  expandedRowKeys={expandedRowsKeys}
                  expandedRowRender={(record, index, indent, expanded) =>
                    expanded && (
                      <Row gutter={16} className={styles.monitoringDropdown}>
                        <Col lg={24} xl={12}>
                          <Title level={5}>
                            List of history data for {record?.name} monitor
                          </Title>
                          <ResultsHistory
                            monitoringQueryResultHistoryStatus={
                              props?.monitoringQueryResultHistoryStatus
                            }
                            monitoringQueryResultHistories={historyDataSource}
                            history={props.history}
                            monitorQuery={record}
                            fileDownload={props?.fileDownload}
                            fileContentCopy={props?.fileContentCopy}
                            copyFileContentStatus={props?.copyFileContentStatus}
                            downloadFileStatus={props?.downloadFileStatus}
                            websocketConnection={props?.websocketConnection}
                          />
                        </Col>

                        <Col lg={24} xl={12}>
                          {expandedChartData?.length > 0 ? (
                            <MonitoringBarChart
                              data={expandedChartData}
                              monitoringQueryResultHistoryStatus={
                                props?.monitoringQueryResultHistoryStatus
                              }
                            />
                          ) : (
                            <div className={styles.noDataOnExpand}>
                              <StopOutlined />
                              <Title level={4}>No data available</Title>
                            </div>
                          )}
                        </Col>
                      </Row>
                    )
                  }
                  expandIcon={({ expanded, onExpand, record }) =>
                    expanded === true ? (
                      <UpOutlined onClick={() => onExpand(record, expanded)} />
                    ) : (
                      <Tooltip title="View Results History">
                        <DownOutlined
                          onClick={() => onExpand(record, expanded)}
                        />
                      </Tooltip>
                    )
                  }
                />
              </>
            ) : (
              <LoadingSilentPush size={80} positionCenter />
            )}
          </div>
        </Fragment>

        <Monitor
          monitorModalVisible={showEditMonitorModal}
          handleModalCancel={handleModalCancel}
          selectedMonitorToEdit={selectedMonitorToEdit}
          websocketConnection={props?.websocketConnection}
        />

        <VersionCheck parentComponentStatus={monitoringQueriesStatus} />
      </div>
    ) : (
      <PermissionDeniedPage />
    )
  ) : (
    <LoadingSilentPush size={80} positionCenter />
  );
}

export default Monitoring;
